// Pure gating logic for the feedback widget's PROACTIVE auto-open, split out of
// the component so the cadence rules are unit-testable without a DOM.
//
// The cadence is keyed to the authenticated user, SERVER-SIDE (fetched from the
// dashoneFeedbackPromptState function into `FeedbackPromptState`) — it replaced
// the old per-device localStorage cap, which drifted per browser and reset on
// cache clear. This module only decides whether the panel may *auto-open*; the
// launcher (FAB) is always present, and a manual open never consults this gate.

/** Mirror of the backend FeedbackPromptState (firebase/functions/src/feedback/types.ts). */
export interface FeedbackPromptState {
  last_offered_at: string | null;
  last_dismissed_at: string | null;
  last_submitted_at: string | null;
}

/** Lifecycle events the widget stamps against the per-user state. */
export type FeedbackPromptEvent = "offered" | "dismissed" | "submitted";

// Transient cross-tab "the panel auto-opened here" claim. This is genuinely
// device-local (it de-dupes the proactive open across a user's open tabs in the
// same session), so it stays in localStorage — it is NOT part of the durable,
// per-user cadence, which lives server-side.
export const FEEDBACK_PROMPT_ACTIVE_KEY = "dashone.feedbackPrompt.active.v1";

const DAY_MS = 24 * 60 * 60 * 1000;
// Cadence windows, differentiated per best practice: a submit earns a long
// cooldown (they engaged), a dismissal a shorter snooze, and a shown-but-ignored
// offer its own cooldown so we don't re-offer every session.
export const SUBMIT_COOLDOWN_MS = 90 * DAY_MS;
export const DISMISS_COOLDOWN_MS = 30 * DAY_MS;
export const OFFER_COOLDOWN_MS = 30 * DAY_MS;

/** True once `now` is at least `windowMs` past the timestamp (null/unparseable = long ago). */
function pastCooldown(ts: string | null, windowMs: number, now: number): boolean {
  if (!ts) return true;
  const t = Date.parse(ts);
  if (Number.isNaN(t)) return true;
  return now - t >= windowMs;
}

/**
 * Decide whether the panel may AUTO-OPEN, given the user's server-side cadence
 * state. A `null` state means the fetch failed — fail toward NOT nagging (the
 * FAB still works on demand). Eligible only when past all three cooldowns.
 * (Manual opens via the launcher are always allowed and never consult this.)
 */
export function proactiveOpenEligible(state: FeedbackPromptState | null, now: number): boolean {
  if (!state) return false;
  return (
    pastCooldown(state.last_offered_at, OFFER_COOLDOWN_MS, now) &&
    pastCooldown(state.last_dismissed_at, DISMISS_COOLDOWN_MS, now) &&
    pastCooldown(state.last_submitted_at, SUBMIT_COOLDOWN_MS, now)
  );
}
